import React, { useState } from 'react';
import { Link } from '@inertiajs/react';
import MainLayout from '../../layouts/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FiCalendar, FiPlus, FiEdit2, FiTrash2 } from 'react-icons/fi';

export default function TourSlotsIndex({ tour_slots = {}, facilities = [], user }) {
  const [selectedFacility, setSelectedFacility] = useState('all');
  
  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    }).format(date);
  };
  
  // Format time
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: 'numeric',
      hour12: true
    }).format(date);
  };
  
  // Get dates in order
  const dates = Object.keys(tour_slots).sort((a, b) => new Date(a) - new Date(b));
  
  // Filter slots by selected facility
  const filteredSlots = selectedFacility === 'all'
    ? tour_slots
    : Object.fromEntries(
        Object.entries(tour_slots).map(([date, slots]) => [
          date,
          slots.filter(slot => slot.facility.id.toString() === selectedFacility)
        ]).filter(([_, slots]) => slots.length > 0)
      );
  
  // Get filtered dates
  const filteredDates = Object.keys(filteredSlots).sort((a, b) => new Date(a) - new Date(b));

  return (
    <MainLayout user={user}>
      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Tour Slots</h1>
              <p className="text-gray-500">Manage available time slots for facility tours</p>
            </div>
            <Button className="mt-4 md:mt-0 flex items-center" asChild>
              <Link href="/facility_owner/tour_slots/new">
                <FiPlus className="mr-2 h-4 w-4" />
                Add New Slot
              </Link>
            </Button>
          </div>
          
          {/* Facility filter */}
          <div className="mb-6">
            <div className="flex flex-wrap gap-2">
              <Button 
                variant={selectedFacility === 'all' ? "default" : "outline"} 
                size="sm"
                onClick={() => setSelectedFacility('all')}
              >
                All Facilities
              </Button>
              
              {facilities.map(facility => (
                <Button
                  key={facility.id}
                  variant={selectedFacility === facility.id.toString() ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedFacility(facility.id.toString())}
                >
                  {facility.name}
                </Button>
              ))}
            </div>
          </div>
          
          {filteredDates.length > 0 ? (
            <div className="space-y-6">
              {filteredDates.map(date => (
                <Card key={date} className="overflow-hidden">
                  <CardHeader className="bg-gray-50">
                    <CardTitle className="flex items-center">
                      <FiCalendar className="mr-2 h-5 w-5 text-blue-600" />
                      {formatDate(date)}
                    </CardTitle>
                  </CardHeader>
                  
                  <CardContent className="divide-y">
                    {filteredSlots[date].map(slot => (
                      <div key={slot.id} className="py-4 flex flex-col md:flex-row md:items-center md:justify-between">
                        <div className="mb-3 md:mb-0">
                          <p className="font-medium">{formatTime(slot.start_at)} - {formatTime(slot.end_at)}</p>
                          <p className="text-sm text-gray-500">{slot.facility.name}</p>
                        </div>
                        
                        <div className="flex items-center space-x-3">
                          <Badge variant={
                            slot.status === 'open' ? 'success' : 
                            slot.status === 'booked' ? 'default' : 'secondary'
                          }>
                            {slot.status === 'open' ? 'Available' : 
                             slot.status === 'booked' ? 'Booked' : 'Closed'}
                          </Badge>
                          
                          <div className="flex space-x-2">
                            <Button variant="outline" size="sm" className="flex items-center" asChild>
                              <Link href={`/facility_owner/tour_slots/${slot.id}/edit`}>
                                <FiEdit2 className="h-4 w-4" />
                              </Link>
                            </Button>
                            
                            <Link 
                              href={`/facility_owner/tour_slots/${slot.id}`} 
                              method="delete" 
                              as="button"
                              className="inline-flex items-center justify-center h-8 px-2 py-1 text-sm font-medium border rounded-md border-gray-300 bg-white hover:bg-gray-50 text-gray-700"
                            >
                              <FiTrash2 className="h-4 w-4 text-red-500" />
                            </Link>
                          </div>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="text-center py-12">
              <CardContent>
                <FiCalendar className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-1">No tour slots available</h3>
                <p className="text-gray-500 mb-4">
                  {selectedFacility !== 'all' 
                    ? "No tour slots for the selected facility." 
                    : "Get started by adding your first tour slot."}
                </p>
                <Button asChild>
                  <Link href="/facility_owner/tour_slots/new">
                    <FiPlus className="mr-2 h-4 w-4" />
                    Add New Slot
                  </Link>
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </MainLayout>
  );
}
